/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useRef, useState} from 'react';
import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import ArgosCollapseComponent from '../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../constants/theme';
import ImageProperties from '../CollapsibleTabs/ImageProperties';
import ItemsReferencing from '../CollapsibleTabs/ItemsReferencing/ItemsReferencing';
import ChangeHistory from '../CollapsibleTabs/ChangeHistory';
import ArgosButton, {ArgosLoadingButton} from '../../../components/Buttons/ArgosButton';
import {useImagePostRequest, usePostRequest, usePostRequestWithJsonResponse} from '../../../../api/helper';
import {addObjectEndpoint, addRealWorldObjectImageEndpoint} from '../../objectApp/constants';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks';
import {selectCurrentUser} from '../../../../redux/ducks/AccessControl/slice';
import {Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField} from '@mui/material';
import MiscellaneousParameterProperties from '../CollapsibleTabs/MiscellaneousParameterProperties';
import {useGetIsUserAdmin, useHasWriteAccessGivenKey} from "../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EArgosRouteKeys} from '../../../../Routes/constants';
import {DELETE_RWO_ENDPOINT, DELETE_RWO_VERSION_TITLE_TEXT, DELETE_RWO_VERSION_WARNING_TEXT} from "../constants";
import {toast} from "react-hot-toast";
import ArgosDialog from "../../../components/Dialogs/ArgosDialog";
import {ETrackedItemTypes} from "../../../../constants/trackedItems";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../hooks/useSetRecentlyViewedWithDeps";
import {ArgosFavoriteIconLogic} from "../../../components/Buttons/ArgosFavoriteIconLogic";
import Tooltip from "@mui/material/Tooltip";
import {ArgosEditIcon} from "../../../components/Icons/ArgosActionIcons";
import {PMO_TYPES_TEXT} from "../PotentiallyMovingObjects/constants";
import AddIcon from "@mui/icons-material/Add";
import { renderObjectId } from '../CommonFields';


const MiscellaneousContent = (props: any) => {
  const classifications = props.object?.objectClassificationList?.split(',') || [];
  const [objectClassification, setObjectClassification] = useState(props.object?.objectClassificationList);

  const triggerReRender = props.triggerReRender
  const navigate = useNavigate();
  const dispatch = useAppDispatch()
  const currentUserGmid = useAppSelector(state => selectCurrentUser(state).id)
  const [objectId, setObjectId] = useState<string>('');
  const [objectName, setObjectName] = useState<string>('');
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; cellKey: string } | null>(null);
  const [tableData, setTableData] = useState({
    description: props?.object?.description || '',
    axisDefinition: props?.object?.axisDefinition || '',
    references: props?.object?.references || '',
    objectRequestSource: props?.object?.objectRequestSource || '',
    comments: props?.object?.comments || ''
  });
  const uploadImageHandle = useImagePostRequest(addRealWorldObjectImageEndpoint);
  const [data, setData] = useState<any>({});
  const [mergedData, setMergedData] = useState<any>({});

  const [imageData, setImageData] = useState<any[]>([]);
  const [fileFormData, setFileFormData] = useState<FormData[]>([]);
  const [isEnabled, setIsEnabled] = useState<any>(false);

  // consts for user permissions and dialog
  const useHasWriteAccessGivenKeyValue = useHasWriteAccessGivenKey(EArgosRouteKeys.MISCELLANEOUS_OBJECTS)
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [isObjectNameUpdated, setIsObjectNameUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const updateRequest = usePostRequestWithJsonResponse(addObjectEndpoint);


  /**
   * Handles updating the object name by sending a request to the backend.
   *
   * This function resets the error message, prepares the request payload,
   * and sends a POST request to update the object name. If successful,
   * it closes the update dialog and refreshes the UI; otherwise, it sets an error message.
   */
  const objectNameRef = useRef<HTMLInputElement>(null);
  const [isUpdatingObjectName, setIsUpdatingObjectName] = useState(false);
  const handleUpdateObjectName = async (oldFieldValue: any, fieldKey: string) => {
    setErrorMessage(""); // Reset error message

    const newObjectName = objectNameRef.current?.value || "";

    const dataToSubmit = {
      oldFieldValue,
      newFieldValue: newObjectName,
      fieldKey,
      type: props?.object?.type,
      objectId: props.object.id,
    }

    try {
      setIsUpdatingObjectName(true)

      // Use the function returned by the hook inside the event handler
      const response = await updateRequest(dataToSubmit, null, "POST", "?action=updateField");

      const responseData = await response.json();  // Read JSON response

      if (!response.ok) {
        throw new Error(responseData.message || "Error updating object name");
      }

      setShowUpdateDialog(false);
      triggerReRender(); // Refresh UI
    } catch (error: any) {
      toast.error("Failed to update name");
      setErrorMessage(error.message);
    } finally {
      setIsUpdatingObjectName(false)
    }
  };


  useEffect(() => {

    // Ensure fileFormData is an array and initialized if needed
    if (!fileFormData || !Array.isArray(fileFormData)) {
      setFileFormData([]);
    } else {
      const updatedFileFormData = [...fileFormData]; // Clone current state

      // Go through each element of the imageData array
      for (let i = 0; i < imageData.length; i++) {
        const element = imageData[i];
        if (Array.isArray(element)) {
          // If the element is an array, iterate through each FormData object inside it
          element.forEach((formData, index) => {
            if (formData instanceof FormData) {
              // Ensure a new FormData is created for each index
              if (!updatedFileFormData[index]) {
                updatedFileFormData[index] = new FormData();
              }

              // Append each key-value pair from FormData to the appropriate index
              for (const [key, value] of formData.entries()) {
                updatedFileFormData[index].append(key, value);
              }
            }
          });
        } else if (element && typeof element === "object" && "pendingImages" in element) {
          const {pendingImages} = element;

          if (Array.isArray(pendingImages)) {
            // Iterate through each pendingImage, which should be a FormData object
            for (let j = 0; j < pendingImages.length; j++) {
              const pendingImage = pendingImages[j];
              if (pendingImage instanceof FormData) {

                // Ensure a new FormData is created for each index
                if (!updatedFileFormData[j]) {
                  updatedFileFormData[j] = new FormData();
                }

                // Append each key-value pair from FormData to the appropriate index
                for (const [key, value] of pendingImage.entries()) {
                  updatedFileFormData[j].append(key, value);
                }
              }
            }
          }
        } else if (element instanceof FormData) {
          // If the element itself is a FormData object

          // Ensure a new FormData is created for this index
          if (!updatedFileFormData[i]) {
            updatedFileFormData[i] = new FormData();
          }

          // Append each key-value pair from FormData to the appropriate index
          for (const [key, value] of element.entries()) {
            updatedFileFormData[i].append(key, value);
          }
        }
      }

      // Set the updated fileFormData state
      setFileFormData(updatedFileFormData);
    }
  }, [imageData]);


  useEffect(() => {
    setObjectId(props?.object?.id|| '');
    setObjectName(props?.object?.objectName || '');
    setObjectClassification(props?.object?.objectClassificationList || '');
    setTableData({
      description: props?.object?.description || '',
      axisDefinition: props?.object?.axisDefinition || '',
      references: props?.object?.references || '',
      objectRequestSource: props?.object?.objectRequestSource || '',
      comments: props?.object?.comments || ''
    });
    createCollapsibleTabs();
  }, [props]);

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {};
    _componentDataMap['Parameters'] = <MiscellaneousParameterProperties onDataUpdate={handleDataUpdate} onCellChangeParams={handleCellChangeParameters} {...props} style={{overflow: 'hidden'}}/>;
    _componentDataMap['Images'] = <ImageProperties onDataUpdate={handleDataUpdate} onImageUpdate={handleImageUpdate} {...props} style={{overflow: 'hidden'}}/>;
    _componentDataMap['Items Referencing Object'] = <ItemsReferencing style={{overflow: 'hidden'}} object={props.object}/>;
    _componentDataMap['Change History'] = <ChangeHistory onDataUpdate={handleDataUpdate} {...props} isSignType={false} style={{overflow: 'hidden'}}/>;
    setComponentDataMap(_componentDataMap);
  };


  const handleDoubleClick = (rowIndex: number, cellKey: string) => {
    setEditableCell({rowIndex, cellKey});
  };

  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>, cellKey: string) => {
    setIsEnabled(true);
    setTableData((prev) => ({
      ...prev,
      [cellKey]: e.target.value
    }));
  };

  const handleBlur = () => {
    setEditableCell(null);
  };

  const handleCellChangeParameters = (newState: boolean) => setIsEnabled(newState);

  const handleDataUpdate = (updatedData: any) => {
    setData((prevState: any) => {
      const mergedData = {
        ...prevState,
        ...(updatedData && Object.keys(updatedData).length > 0 ? updatedData : {}),
      };

      return mergedData;
    });
  };


  const handleImageUpdate = (updatedImage: FormData) => {
    setIsEnabled(true);
    // Set the state directly to updatedImage
    setImageData([updatedImage]);
  };

  const handleSubmit = async () => {
    setIsEnabled(false);
    // Create a new FormData object
    const combinedFormData = new FormData();

    fileFormData.forEach((formData, index) => {
      formData.forEach((value, key) => {
        // Check if the key is 'file'
        if (key === 'file') {
          combinedFormData.append('file', value); // Append the file directly as 'file'
        } else {
          const uniqueKey = `${index}[${key}]`;
          combinedFormData.append(uniqueKey, value);
        }
      });
    });

    const extendedData = {
      ...data,
      ...tableData,
    };

    const dataToSubmit = {
      ...extendedData,
      objectName,
      objectType: props.object.type,
      mode: "newVersion",
      author: currentUserGmid,
      id: props?.object?.id,
      latestVersion: props?.object?.latestVersion,
    };

    // Append dataToSubmit as JSON to combinedFormData
    combinedFormData.append(
      "json",
      new Blob([JSON.stringify(dataToSubmit)], {type: "application/json"})
    );

    // Print all the contents of the combinedFormData
    for (let pair of combinedFormData.entries()) {
      if (pair[1] instanceof Blob) {
        const reader = new FileReader();
        reader.readAsText(pair[1] as Blob);
      }
    }

    try {
      const response = await uploadImageHandle(combinedFormData);
      if (response?.updated_items) {
        const id = Array.isArray(response.updated_items)
          ? response.updated_items[0]?.id
          : response.updated_items?.id;

        if (props.fetchBaselineData) {
          props.fetchBaselineData(id);
        }
      }
    } catch (error) {
      console.error("Failed to add new version:", error);
      toast.error("Failed to add new version");
      setIsEnabled(true)
    }
  };


  useEffect(() => {
    const defaultClassification = classifications[0]?.trim() || '';
    if (!objectClassification && defaultClassification) {
      setObjectClassification(defaultClassification);
    }
  }, [classifications]);


  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");


  const handleDoubleClickClassification = () => {
    setInputVisible(true); // Show input field on double-click
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setInputVisible(false); // Hide input on blur if no enter is pressed
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      // Append the input value to the existing classifications with a comma separator
      setObjectClassification((prevClassification: any) => `${prevClassification}, ${inputValue.trim()}`);

      setInputValue(""); // Clear the input value
      setInputVisible(false); // Hide the input field
    }
  };

  const [isLoadingDeleteRwoVersion, setIsLoadingDeleteRwoVersion] = useState(false);
  const deleteRwoVersionHandle = usePostRequest(DELETE_RWO_ENDPOINT)
  const [isDeleteRwoVersionDialogOpen, setIsDeleteRwoVersionDialogOpen] = useState(false);
  const onClickDeleteRwoVersion = (e: any) => {
    setIsDeleteRwoVersionDialogOpen(true)
  }
  /**
   * Handles the deletion of an rwo version.
   * @param e - The event object.
   */
  const handleDeleteRwoVersion = (e: any) => {
    if (!props.object?.id) {
      toast.error("RWO ID is not available");
      return;
    } else if (Number(props.object?.latestVersion) === 1) {
      // toast.error("Cannot delete the first version of the object");
      // return;
    }
    const payload = {
      deleteType: 'rwoVersion',
      rwoId: props.object?.id,
    }
    setIsLoadingDeleteRwoVersion(true)
    deleteRwoVersionHandle(payload).then((res: any) => {
      // if res has error key, then show error message
      if (res?.error) {
        // toast.error(res.error)
        toast.error((t) => (
          <div>
            <span>{res.error}</span>
            <ArgosButton
              style={{marginLeft: 5}}
              variant={'text'}
              onClick={() => toast.dismiss(t.id)}
            >
              Close
            </ArgosButton>
          </div>
        ), {duration: Infinity})
        return
      } else {
        props.fetchBaselineData()
      }
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to delete object version")
    }).finally(() => {
      setIsLoadingDeleteRwoVersion(false)
    })
  }


  /**
   * Favorites and recently viewed
   */
  const trackedItemUid = props.object?.id
  const trackedItemType = ETrackedItemTypes.MISCELLANEOUS_OBJECTS
  const favIcon = <ArgosFavoriteIconLogic favoriteType={trackedItemType} trackedItemUid={trackedItemUid} partitionKey={trackedItemUid}/>
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid,
    partitionKey: trackedItemUid,
    trackedItemType,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)

  const columns = [
    { label: "Description", key: "description" },
    { label: "Axis Definitions", key: "axisDefinition" },
    { label: "References", key: "references" },
    { label: "Object Request Source", key: "objectRequestSource" },
    { label: "Comments", key: "comments" },
  ];

  const inputStyle = {
    width: '100%', height: '48px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '56px'
  };
  return (
    <div style={{overflow: 'auto'}}>
      <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
        <div style={{padding: '10px'}}>
          {/*Object name*/}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '100%'}}>
            <div style={{display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'center'}}>
              {/* Object Name Input */}
              <div style={{flex: 1, maxWidth: '60%', display: 'flex', alignItems: 'center'}}>
                <div style={{flexGrow: 1}}>
                  <label htmlFor="objectName" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                    Object Name
                  </label>
                  <input
                    id="objectName"
                    type="text"
                    disabled={true}
                    value={objectName}
                    style={inputStyle}
                  />
                </div>

                {/* Popup Dialog for Updating Object Name */}
                <Dialog
                  open={showUpdateDialog}
                  onClose={() => {
                  }}
                  TransitionProps={{
                    onEntered: () => {
                      if (objectNameRef.current) {
                        objectNameRef.current.value = objectName; // Set the default value
                        objectNameRef.current.focus()
                      }
                    },
                  }}
                >
                  <DialogTitle>Update Object Name</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Enter a new name for the object. This will replace the name for all versions of this object.
                    </DialogContentText>
                    <TextField
                      style={{marginTop: 20}}
                      focused={true}
                      margin="dense"
                      label="Object Name"
                      type="text"
                      fullWidth
                      inputRef={objectNameRef}
                      error={!!errorMessage}
                      helperText={errorMessage}
                    />
                  </DialogContent>
                  <DialogActions>
                    <ArgosButton disabled={isUpdatingObjectName} onClick={() => setShowUpdateDialog(false)} color="secondary">
                      Cancel
                    </ArgosButton>
                    <ArgosLoadingButton isLoading={isUpdatingObjectName} onClick={() => handleUpdateObjectName(props?.object?.objectName, 'objectName')} color="primary">
                      Save
                    </ArgosLoadingButton>
                  </DialogActions>
                </Dialog>

              </div>

              {/* Update Object Name Button for triggering Dialog and allowing user to edit an object */}
              {useHasWriteAccessGivenKeyValue && (
                <ArgosFlexbox style={{marginTop: 20}}>
                  <Tooltip title='Update Object Name' placement='top' arrow>
                    <IconButton
                      style={{width: 60, height: 60}}
                      onClick={() => {
                        setErrorMessage("");
                        setShowUpdateDialog(true);
                      }}
                    >
                      <ArgosEditIcon style={{width: 30, height: 30}}/>
                    </IconButton>
                  </Tooltip>
                </ArgosFlexbox>
              )}
              {/* Object ID Input */}
              {renderObjectId({ objectId: objectId ?? null, inputStyle })}
              {/*Favorite*/}
              <div style={{marginLeft: 10, alignSelf: 'flex-start', marginTop: 40}}>
                {favIcon}
              </div>
            </div>
          </div>

          {/*Delete and add version buttons; favorite button*/}
          <ArgosFlexbox style={{width: '100%', marginBottom: 20, padding: '10px 10px 10px 0', alignItems: 'center'}}>
            <ArgosFlexbox style={{ alignSelf: 'flex-start'}}>
              <ArgosButton
                onClick={onClickDeleteRwoVersion}
                style={{
                  minWidth: '180px',
                  height: '48px'
                }}
              >
                Delete Version
              </ArgosButton>

              {/* Save New Version Button */}
              {useHasWriteAccessGivenKeyValue && (
                <ArgosButton
                  onClick={handleSubmit}
                  // disabled={!isEnabled}
                  style={{
                    minWidth: '180px',
                    height: '48px'
                  }}
                >
                  Save New Version
                </ArgosButton>
              )}


            </ArgosFlexbox>
          </ArgosFlexbox>

          {/* Table Section */}
          <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '20px'}}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{ border: "1px solid #ccc", padding: "8px" }}
                    onDoubleClick={() => handleDoubleClick(0, column.key)}
                  >
                    {editableCell?.rowIndex === 0 && editableCell?.cellKey === column.key ? (
                      <input
                        type="text"
                        value={tableData[column.key as keyof typeof tableData]}
                        onChange={(e) => handleCellChange(e, column.key)}
                        onBlur={() => {
                          setTableData((prev) => ({
                            ...prev,
                            [column.key]: prev[column.key as keyof typeof tableData].trim() === "" ? "" : prev[column.key as keyof typeof tableData],
                          }));
                          handleBlur();
                        }}
                        autoFocus
                        style={{ width: "100%", padding: "4px" }}
                      />
                    ) : (
                      (!tableData[column.key as keyof typeof tableData] || tableData[column.key as keyof typeof tableData].trim() === "")
                        ? "N/A"
                        : tableData[column.key as keyof typeof tableData]
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{flex: 1, overflow: 'auto'}}>
          <ArgosCollapseComponent
            styleProps={{
              outlineOnly: true,
              fontColor: theme.layout.sidebar.backgroundColor,
            }}
            defaultOpen={true}
            data={componentDataMap ?? {}}
          />
        </div>
      </ArgosFlexbox>

      <ArgosDialog
        open={isDeleteRwoVersionDialogOpen}
        width={'auto'} maxWidth={'300px'} height={'auto'}
        headerTitle={DELETE_RWO_VERSION_TITLE_TEXT} headerTitleAlign={'left'}
      >
        <span>{DELETE_RWO_VERSION_WARNING_TEXT}</span>
        <ArgosFlexbox justifyContent={'center'} style={{marginTop: 30}}>
          <ArgosButton disabled={isLoadingDeleteRwoVersion} onClick={() => setIsDeleteRwoVersionDialogOpen(false)}>Exit</ArgosButton>
          <ArgosLoadingButton isLoading={isLoadingDeleteRwoVersion} onClick={handleDeleteRwoVersion}>Ok</ArgosLoadingButton>
        </ArgosFlexbox>
      </ArgosDialog>
    </div>
  );


};

export default MiscellaneousContent;
